import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import * as Animatable from 'react-native-animatable';
import TransformableImage from 'src/presentation/components/molecules/TransformableImage';
import ImageMapToaster from 'src/presentation/components/molecules/ImageMapToaster';
import { useFooterHeight } from 'src/presentation/hooks/useFooterHeight';
import { useContentWidth } from 'src/presentation/hooks/useContentWidth';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useMapStore, type MapHotspot } from 'src/domain/stores/useMapStore';
import Colors from 'src/core/utils/Colors';
import type { ApiPage } from 'src/core/models/api/ApiPage';

interface ImageMapProps {
	data: {
		hotspots: MapHotspot[];
		floorplan_color: string;
		floorplan_image: string;
		hotspot_style: string;
	};
	onLocationDetailClick: (toastUrl: ApiPage) => void;
}

export default function ImageMap({ data, onLocationDetailClick }: ImageMapProps) {
	const [, setMapLoaded] = useState(false);
	const [toastUrl, setToastUrl] = useState<ApiPage | null>(null);
	const footerHeight = useFooterHeight();
	const contentWidth = useContentWidth();

	const eventStore = useEventStore();
	const markerData = useMapStore((state) => state.markerData) as { pageID?: string | string[] } | null;
	const showMarkerInfoFooter = useMapStore((state) => state.showMarkerInfoFooter);
	const setShowMarkerInfoFooter = useMapStore((state) => state.setShowMarkerInfoFooter);
	const mapHasInteraction = useMapStore((state) => state.mapHasInteraction);
	const setHotspots = useMapStore((state) => state.setHotspots);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		setHotspots(data.hotspots);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data.hotspots]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		if (markerData?.pageID !== undefined) {
			// pageID is a page path (e.g.
			const ids = Array.isArray(markerData.pageID) ? markerData.pageID : String(markerData.pageID).split('/');
			setToastUrl(eventStore.getPageByID(ids));
		} else {
			setToastUrl(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [markerData?.pageID]);

	return (
		<View style={{ flex: 1, overflow: 'hidden' }}>
			<TransformableImage
				hotspotStyle={data.hotspot_style}
				pixels={{ width: 4000, height: 4000 }}
				source={{ uri: data.floorplan_image, cache: 'force-cache' }}
				style={{ flex: 1, backgroundColor: data.floorplan_color }}
				onLoadEnd={() => setMapLoaded(true)}
			/>
			<Animatable.View
				animation={showMarkerInfoFooter ? 'slideInUp' : undefined}
				duration={180}
				easing="ease-in-out-cubic"
				style={{
					position: 'absolute',
					zIndex: 3,
					// The Footer ("Powered by ...") is absolute at bottom:0 with height 35 + the bottom safe-area inset and
					// a higher zIndex; lift the toaster by the full footer height so it never falls behind the footer (incl.
					bottom: footerHeight,
					width: contentWidth,
					height: mapHasInteraction ? 80 : 0,
					overflow: 'hidden',
					backgroundColor: Colors.white,
				}}
				useNativeDriver={true}
				onAnimationEnd={() => setShowMarkerInfoFooter(false)}
			>
				<ImageMapToaster toastUrl={toastUrl} onLocationDetailClick={onLocationDetailClick} />
			</Animatable.View>
		</View>
	);
}
